import Box from '@mui/material/Box/Box'
import Radio from '@mui/material/Radio/Radio'
import Stack from '@mui/material/Stack/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useBookingContext } from '@providers/booking/booking_context'
import type { TCartGroupOffer } from '@providers/booking/types'
import { i18WithParams as t } from '@shared/locale'
import { DiscountTag } from 'components-ui/src/discount_tag/discount_tag'
import { getRoomPrice } from 'components-ui/src/offer_card/helper'
import { formatNumberAsPrice } from 'components-ui/src/shared/common'
import { AvailabilityGroupClientDto, BookingSearchClientDto } from 'service-api/src/types/api'

interface TRequiredAddonProps {
  offer?: BookingSearchClientDto
  groupIndex: number
  petIndex: number
  onChange?: (offer: TCartGroupOffer) => void
}

export const RequiredAddon = ({ offer, groupIndex, petIndex, onChange }: TRequiredAddonProps) => {
  const bookingCtx = useBookingContext()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  if (!offer) {
    return null
  }

  const addonSelected = bookingCtx.state.cart.groups[groupIndex].requiredAddOns[petIndex]
  const handleChange = () => {
    onChange?.({
      offer: offer.offer!,
      availability: offer.availabilityGroups![0],
    })
  }

  const controlProps = (itemId: string) => ({
    checked: addonSelected?.offer.id === itemId,
    onChange: handleChange,
    value: itemId,
  })
  // Get the first availability group since required add-ons only have one
  // availability group because the offer search if for the pets in the current room (group).
  const group = (offer.availabilityGroups as AvailabilityGroupClientDto[])?.[0] || []
  const totalPets = bookingCtx.state.cart.groups[bookingCtx.state.currentGroup]?.pets.length
  const strategyStr =
    group.pricingStrategy === 'NIGHTLY' ? t('global.label.strategy.night') : t('global.label.strategy.day')
  const isAvailable = Boolean(group.availabilities?.[0]?.available)
  const priceVaries = Boolean(offer.offer?.priceVaries)
  const addOnPrice = getRoomPrice(group, false, totalPets) || 0

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
      <Stack direction="row" gap={2} alignItems="center" sx={{ width: isBelowMD ? '100%' : 'auto' }} flexGrow={2}>
        <Box flexShrink={0}>
          {isAvailable ? (
            <Radio {...controlProps(offer.offer?.id || '')} />
          ) : (
            <Box sx={{ width: '42px', height: '100%' }}>&nbsp;</Box>
          )}
        </Box>

        <Stack direction="column" gap={0.5} flex={1} sx={{ maxWidth: '600px' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography variant="h6" color="text.primary">
              {offer.offer?.displayName || ''}
            </Typography>
            <DiscountTag
              discounts={group.availabilities?.[0]?.price?.discounts || []}
              eligibleForConsumableBenefits={offer.eligibleForConsumableBenefits}
            />
          </Stack>
          <Typography variant="body1" color="text.primary">
            {offer.offer?.description || ''}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" gap={2} alignItems="center" sx={{ width: isBelowMD ? '100%' : 'auto' }}>
        <Stack direction="row" gap={2} alignItems="center" sx={{ width: isBelowMD ? '100%' : 'auto' }}>
          <Box flexShrink={0} display={isBelowMD ? 'block' : 'none'}>
            <Box sx={{ width: '42px', height: '100%' }}>&nbsp;</Box>
          </Box>
          <Stack direction="column" gap={0} sx={{ minWidth: isBelowMD ? '100%' : 'auto' }}>
            {!isAvailable && (
              <Stack
                direction="row"
                gap={0}
                alignItems="center"
                justifyContent={isBelowMD ? 'flex-start' : 'flex-end'}
                sx={{ minWidth: isBelowMD ? '100%' : 'auto' }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('searchResults.requiredAddons.notAvailable')}
                </Typography>
              </Stack>
            )}
            {isAvailable && addOnPrice === 0 && (
              <Stack
                direction="row"
                gap={0}
                alignItems="center"
                justifyContent={isBelowMD ? 'flex-start' : 'flex-end'}
                sx={{ minWidth: isBelowMD ? '100%' : 'auto' }}
              >
                <Typography variant="body2bold" color="text.primary">
                  {t('searchResults.requiredAddons.included')}
                </Typography>
              </Stack>
            )}
            {isAvailable && addOnPrice !== 0 && (
              <Stack
                direction="row"
                gap={0}
                alignItems="center"
                justifyContent={isBelowMD ? 'flex-start' : 'flex-end'}
                sx={{ minWidth: isBelowMD ? '100%' : 'auto' }}
              >
                {priceVaries && (
                  <Typography variant="caption" component="span" sx={{ paddingRight: '4px' }}>
                    {t('offers.startingAt')}
                  </Typography>
                )}
                <Typography variant="body1" component="span" color="text.primary">
                  +
                </Typography>
                <Typography variant="h6" color="text.primary">
                  {formatNumberAsPrice(addOnPrice)}
                </Typography>
                <Typography variant="caption" component="span">
                  /<span>{strategyStr}</span>
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}
